import React from 'react';
import { defineMessages, type IntlShape } from 'react-intl-next';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type {
	ExtensionComponentProps,
	ExtensionManifest,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import { messages as quickInsertMessages } from '@atlaskit/editor-common/quick-insert/messages';
import { ConfluenceAttributionIcon } from '@atlaskit/editor-common/quick-insert/confluence-attribution-icon';
import Lozenge from '@atlaskit/lozenge/lozenge';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { EXTENSION_KEY, EXTENSION_TYPE } from '../nativeTabsConstants.ts';
import type {
	GetCopyLinkBaseUrl,
	NativeTabsParameters,
	ResolveElementByHash,
} from '../nativeTabsTypes.ts';
import { buildDefaultParameters } from '../utils/tabParameters.ts';

import { buildBaseManifest } from './baseManifest.tsx';
import { getNativeTabsBlockTransform } from './blockTransform.ts';
import { getOrientationToolbar } from './orientationToolbar.tsx';

const i18n: {
	newLozenge: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	newLozenge: {
		id: 'native-tabs.extension.new-lozenge',
		defaultMessage: 'New',
		description: 'Text in lozenge next to the Native Tabs quick-insert item.',
	},
});

type NativeTabsEditorManifestOptions = {
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	getCopyLinkBaseUrl?: GetCopyLinkBaseUrl;
	resolveElementByHash?: ResolveElementByHash;
	shouldEnableBlockTransform?: boolean;
	shouldEnableVerticalTabs?: boolean;
};

/**
 * Editor-side manifest for the Native Tabs extension. Adds the slash-menu
 * `quickInsert` entry and points `render()` at `TabsEditor`.
 *
 * The renderer-side counterpart lives in `rendererManifest.tsx`.
 */
export const getNativeTabsEditorManifest = (
	intl: IntlShape,
	{
		createAnalyticsEvent,
		getCopyLinkBaseUrl,
		resolveElementByHash,
		shouldEnableBlockTransform = expVal('confluence_native_tabs_m15', 'isEnabled', false),
		shouldEnableVerticalTabs = expVal('confluence_native_tabs_m2', 'isEnabled', false),
	}: NativeTabsEditorManifestOptions = {},
): ExtensionManifest<NativeTabsParameters> => {
	const newLozengeLabel = intl.formatMessage(i18n.newLozenge);
	const { icon: NativeTabsIcon, manifest: base } = buildBaseManifest(intl);

	return {
		...base,
		modules: {
			...(shouldEnableVerticalTabs
				? { contextualToolbars: [getOrientationToolbar(intl, createAnalyticsEvent)] }
				: {}),
			quickInsert: [
				{
					key: EXTENSION_KEY,
					preview: {
						image: {
							dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVH0.png',
							light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVKG.png',
						},
						attribution: {
							name: intl.formatMessage(quickInsertMessages.previewAttributionConfluence),
							icon: ConfluenceAttributionIcon,
						},
					},
					title: base.title,
					description: base.description,
					lozenge: (
						<Lozenge
							appearance={
								fg('confluence_fronend_labels_categorization_migration') ? 'discovery' : 'new'
							}
						>
							{newLozengeLabel}
						</Lozenge>
					),
					icon: async () => NativeTabsIcon,
					action: async () => {
						const { tabs, frames } = buildDefaultParameters();
						return {
							type: 'multiBodiedExtension',
							attrs: {
								extensionType: EXTENSION_TYPE,
								extensionKey: EXTENSION_KEY,
								parameters: {
									extensionTitle: base.title,
									tabs,
								},
							},
							content: frames,
						};
					},
				},
			],
			nodes: {
				default: {
					type: 'multiBodiedExtension',
					...(shouldEnableBlockTransform
						? { blockTransform: getNativeTabsBlockTransform(intl) }
						: {}),
					// @ts-expect-error — `__allowBodiedOverride` is recognised by the MBE plugin
					// at runtime but isn't yet on `ExtensionModuleNode`. Remove when upstream types update.
					__allowBodiedOverride: true,
					// editor-core's MBE plugin mounts this with `{ node, actions, ... }` injected.
					render: async () => {
						const mod = await import(
							/* webpackChunkName: "loadable-NativeTabsEditor" */ '../components/TabsEditor.tsx'
						);
						return (
							props: ExtensionComponentProps<NativeTabsParameters>,
						): React.JSX.Element | null => {
							if (!props.actions) {
								return null;
							}

							return (
								<mod.TabsEditor
									{...props}
									actions={props.actions}
									getCopyLinkBaseUrl={getCopyLinkBaseUrl}
									resolveElementByHash={resolveElementByHash}
								/>
							);
						};
					},
				},
			},
		},
	};
};
